import { useCallback, useEffect, useMemo, useState } from 'react';
import { getFlujosEntrada, getFlujosEntradas } from '../services/apis';
import { normalizeFlujosEntrada, normalizeFlujosEntradas } from '../utils/flujosEntradasMapper';
import { cajasPalet } from '../utils/mockFlujosData';

const buildErrorMessage = (error) => {
  const message = error?.response?.data?.message || error?.message;

  if (Array.isArray(message)) {
    return message.join(', ');
  }

  return message || 'No se han podido cargar las entradas.';
};

const unwrapFlujosEntradaResponse = (response) =>
  response?.data?.entrada ||
  response?.data?.item ||
  response?.data ||
  response?.entrada ||
  response?.item ||
  response;

export const useFlujosEntradas = (params = {}, options = {}) => {
  const [state, setState] = useState({
    rows: [],
    raw: null,
    loading: true,
    error: '',
  });
  const serializedParams = useMemo(() => JSON.stringify(params), [params]);
  const calculationCajasPalet = options.cajasPalet?.length ? options.cajasPalet : cajasPalet;
  const serializedCajasPalet = useMemo(() => JSON.stringify(calculationCajasPalet), [calculationCajasPalet]);
  const serializedAlmacenes = useMemo(() => JSON.stringify(options.almacenes || []), [options.almacenes]);
  const serializedVariedades = useMemo(() => JSON.stringify(options.variedades || []), [options.variedades]);

  const fetchEntradas = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: '' }));

    try {
      const parsedParams = JSON.parse(serializedParams);
      const parsedCajasPalet = JSON.parse(serializedCajasPalet);
      const parsedAlmacenes = JSON.parse(serializedAlmacenes);
      const parsedVariedades = JSON.parse(serializedVariedades);
      const response = await getFlujosEntradas(parsedParams);

      setState({
        rows: normalizeFlujosEntradas(response, {
          almacenes: parsedAlmacenes,
          variedades: parsedVariedades,
          cajasPalet: parsedCajasPalet,
        }),
        raw: response,
        loading: false,
        error: '',
      });
    } catch (error) {
      setState({
        rows: [],
        raw: null,
        loading: false,
        error: buildErrorMessage(error),
      });
    }
  }, [serializedAlmacenes, serializedCajasPalet, serializedParams, serializedVariedades]);

  useEffect(() => {
    fetchEntradas();
  }, [fetchEntradas]);

  return {
    ...state,
    refetch: fetchEntradas,
  };
};

export const useFlujosEntrada = (id, options = {}, reloadToken = 0) => {
  const [state, setState] = useState({
    entrada: null,
    raw: null,
    loading: true,
    error: '',
  });

  const calculationCajasPalet = options.cajasPalet?.length ? options.cajasPalet : cajasPalet;
  const serializedCajasPalet = useMemo(() => JSON.stringify(calculationCajasPalet), [calculationCajasPalet]);
  const serializedAlmacenes = useMemo(() => JSON.stringify(options.almacenes || []), [options.almacenes]);
  const serializedVariedades = useMemo(() => JSON.stringify(options.variedades || []), [options.variedades]);

  useEffect(() => {
    let isMounted = true;

    const fetchEntrada = async () => {
      setState((current) => ({ ...current, loading: true, error: '' }));

      try {
        const response = await getFlujosEntrada(id);
        const rawEntrada = unwrapFlujosEntradaResponse(response);
        const parsedCajasPalet = JSON.parse(serializedCajasPalet);
        const parsedAlmacenes = JSON.parse(serializedAlmacenes);
        const parsedVariedades = JSON.parse(serializedVariedades);

        if (isMounted) {
          setState({
            entrada: normalizeFlujosEntrada(rawEntrada, {
              almacenes: parsedAlmacenes,
              variedades: parsedVariedades,
              cajasPalet: parsedCajasPalet,
            }),
            raw: response,
            loading: false,
            error: '',
          });
        }
      } catch (error) {
        if (isMounted) {
          setState({
            entrada: null,
            raw: null,
            loading: false,
            error: buildErrorMessage(error),
          });
        }
      }
    };

    fetchEntrada();

    return () => {
      isMounted = false;
    };
  }, [id, reloadToken, serializedAlmacenes, serializedCajasPalet, serializedVariedades]);

  return state;
};
